import { rpcSlice } from '../../../store/api/rpcSlice';

// --- Types ---

export interface PromptOverride {
  slug: string;
  text: string;
}

export interface RunPromptTestRequest {
  profile_slug: string;
  user_message: string;
  fixture_set_id?: string;
  prompt_override?: PromptOverride;
  provider?: string;
  model?: string;
  draft_bundle_id?: string;
}

export interface TestRunError {
  message: string;
  type: 'timeout' | 'rate_limit' | 'content_filter' | 'provider_error' | 'internal';
}

export interface ToolCallRecord {
  name: string;
  arguments: string;
  result: string;
  error?: string;
}

export interface ResolvedPromptRef {
  name: string;
  version: number;
  resolved_id: string;
}

export interface RunPromptTestResponse {
  output: string;
  tool_calls?: ToolCallRecord[];
  latency_ms?: number | null;
  cost_usd?: number;
  resolved_prompts?: ResolvedPromptRef[];
  has_fixture_context: boolean;
  has_document_context: boolean;
  document_count: number;
  error?: TestRunError;
}

export interface EvalFixtureSet {
  id: string;
  name: string;
  version: number;
  description: string;
  identity_context?: Record<string, unknown>;
  document_context?: Record<string, unknown>;
  template_variables: Record<string, unknown>;
  authoring_config?: unknown;
  archived_at?: string | null;
  created_at?: string;
  updated_at: string;
}

export interface ScenarioGroupSummary {
  slug: string;
  display_name: string;
  description: string;
  use_case?: string;
  unavailable_reason?: string;
  compatible_profiles: string[];
  min_documents: number;
  default_documents: number;
  max_documents: number;
}

export interface ListScenarioGroupsResponse {
  groups: ScenarioGroupSummary[];
}

export interface CreateFixtureFromScenarioGroupRequest {
  group_slug: string;
  profile_slug: string;
  seed?: string;
  document_count?: number;
  name: string;
  description: string;
}

export interface CreateFixtureJobResponse {
  job_id: string;
}

export interface FixtureCreationResult {
  fixture_set_id: string;
  session_count: number;
  preview?: Record<string, unknown>;
  anonymization_report_id?: string;
  scenario_group_slug?: string;
  anonymization_status?: string;
  selected_candidates?: Array<{
    session_id: string;
    session_attachment_id: string;
    transcript_content_id: string;
    drive_file_id: string;
    match_reason: string;
  }>;
}

export interface FixtureCreationStatus {
  job_id: string;
  status: 'running' | 'completed' | 'failed';
  current_pass: number;
  pass_name: string;
  total_passes: number;
  error?: string;
  error_code?:
    | 'insufficient_candidates'
    | 'anonymization_failed'
    | 'content_unavailable'
    | 'transcript_access_denied'
    | 'service_unavailable'
    | 'internal';
  result?: FixtureCreationResult;
}

// --- API ---

export const promptTestingApi = rpcSlice.injectEndpoints({
  endpoints: (builder) => ({
    runPromptTest: builder.mutation<RunPromptTestResponse, RunPromptTestRequest>({
      query: (body) => ({
        url: '/prompt_testing.run',
        method: 'POST',
        body,
      }),
    }),

    listFixtureSets: builder.query<{ fixture_sets: EvalFixtureSet[] }, void>({
      query: () => '/prompt_testing.fixture_sets',
    }),

    listScenarioGroups: builder.query<ListScenarioGroupsResponse, void>({
      query: () => '/prompt_testing.scenario_groups',
    }),

    createFixtureFromScenarioGroup: builder.mutation<
      CreateFixtureJobResponse,
      CreateFixtureFromScenarioGroupRequest
    >({
      query: (body) => ({
        url: '/prompt_testing.create_fixture_from_scenario_group',
        method: 'POST',
        body,
      }),
    }),

    getFixtureCreationStatus: builder.query<FixtureCreationStatus, string>({
      query: (jobId) => `/prompt_testing.fixture_creation_status?job_id=${jobId}`,
    }),
  }),
  overrideExisting: false,
});

export const {
  useRunPromptTestMutation,
  useListFixtureSetsQuery,
  useListScenarioGroupsQuery,
  useCreateFixtureFromScenarioGroupMutation,
  useLazyGetFixtureCreationStatusQuery,
} = promptTestingApi;
